import { LoadingOutlined } from "@ant-design/icons";
import { Alert, Button, Card, Flex, Input, Typography, message } from "antd";
import type React from "react";
import { useCallback, useEffect, useState } from "react";
import { getCptCodeByPsychotherapyMinutes } from "../../domain/notes.domain";
import { isNonBehavioralHealthUser } from "../../domain/user.domain";
import { useAppDispatch, useAppSelector } from "../../hooks/redux-hooks";
import { getNotesDetails, updateProceduralCode } from "../../service/notes.service";
import { setShowSettingsModal } from "../../slices/appStateSlice";
import { setNotesDetails } from "../../slices/notesSlice";
import {
  DiagnosticCodeStatus,
  ProceduralCodeStatus,
  type TDiagnosticCode,
  type TNotes,
  type TProceduralCode,
} from "../../types/index.type";
import { EventType, trackEvent } from "../../utils/analytics";
import { POLLING_INTERVAL_DIAGNOSTIC_CODING, POLLING_INTERVAL_PROCEDURAL_CODING } from "../../utils/constants";
import {
  buildDiagnosticCodesText,
  buildProceduralCodesText,
  formatTimeInLocalTimezone,
} from "../../utils/datamanipulation.utils";
import { default as CopyButton, default as NoteCopyButton } from "../CopyButton/CopyButton.component";
import TransferToEHRButton from "../TransferButton/TransferButton.component";
import styles from "./proceduralcoding.module.scss";

interface ProceduralCodingProps {
  notesDetails: TNotes;
}

const { TextArea } = Input;

const ProceduralCoding: React.FC<ProceduralCodingProps> = ({ notesDetails: propNotesDetails }) => {
  const componentName = "proceduralcoding";
  const dispatch = useAppDispatch();
  const [editingCodeId, setEditingCodeId] = useState<number | null>(null);
  const [editingMinutes, setEditingMinutes] = useState<string>("");
  const [editingRationale, setEditingRationale] = useState<string>("");
  const [isSaving, setIsSaving] = useState(false);
  const { userInfo } = useAppSelector((state) => state.auth);
  const { notesDetails } = useAppSelector((state) => state.notes);

  // Use Redux store version if available, fallback to prop
  const currentNotesDetails = notesDetails || propNotesDetails;
  const noteId = currentNotesDetails?.id;
  const proceduralStatus = currentNotesDetails?.procedural_code_status;
  const diagnosticStatus = currentNotesDetails?.diagnostic_code_status;
  const proceduralCodes = currentNotesDetails?.procedural_codes || [];
  const diagnosticCodes = currentNotesDetails?.diagnostic_codes || [];

  const isLoadingCodes = proceduralStatus === ProceduralCodeStatus.PENDING;
  const isLoadingDiagnosticCodes = diagnosticStatus === DiagnosticCodeStatus.PENDING;

  // Separate effect for polling when status is PENDING
  useEffect(() => {
    if (!noteId || proceduralStatus !== ProceduralCodeStatus.PENDING) {
      return;
    }

    let pollCount = 0;
    const maxPolls = 24; // 2 mins (24 * 5 seconds)

    const interval = setInterval(async () => {
      pollCount++;

      try {
        const res = await getNotesDetails(noteId.toString());
        const updatedNote = res.data;

        // Check if status has changed (completed or failed)
        if (updatedNote.procedural_code_status !== ProceduralCodeStatus.PENDING) {
          clearInterval(interval);
          dispatch(setNotesDetails(updatedNote));
          return;
        }

        // Check if we've reached max polls (timeout)
        if (pollCount >= maxPolls) {
          clearInterval(interval);
          dispatch(setNotesDetails(updatedNote)); // Update with latest data
          return;
        }
      } catch (error) {
        console.error("Error during polling:", error);
        if (pollCount >= maxPolls) {
          clearInterval(interval);
          return;
        }
      }
    }, POLLING_INTERVAL_PROCEDURAL_CODING);

    return () => {
      clearInterval(interval);
    };
    // we want to re-run this effect only when the id or the status changes
  }, [noteId, proceduralStatus, dispatch]);

  useEffect(() => {
    if (!noteId || diagnosticStatus !== DiagnosticCodeStatus.PENDING) {
      return;
    }

    let pollCount = 0;
    const maxPolls = 24;

    const interval = setInterval(async () => {
      pollCount++;

      try {
        const res = await getNotesDetails(noteId.toString());
        const updatedNote = res.data;

        if (updatedNote.diagnostic_code_status !== DiagnosticCodeStatus.PENDING) {
          clearInterval(interval);
          dispatch(setNotesDetails(updatedNote));
          return;
        }

        if (pollCount >= maxPolls) {
          clearInterval(interval);
          dispatch(setNotesDetails(updatedNote));
          return;
        }
      } catch (error) {
        console.error("Error during diagnostic polling:", error);
        if (pollCount >= maxPolls) {
          clearInterval(interval);
          return;
        }
      }
    }, POLLING_INTERVAL_DIAGNOSTIC_CODING);

    return () => {
      clearInterval(interval);
    };
    // we want to re-run this effect only when the id or the status changes
  }, [noteId, diagnosticStatus, dispatch]);

  const handleEditCode = useCallback((code: TProceduralCode) => {
    setEditingCodeId(code.id);
    setEditingMinutes(code.additional_fields?.psychotherapy_duration_minutes?.toString() || "");
    setEditingRationale(code.rationale || "");
  }, []);

  const handleCancelEdit = useCallback(() => {
    setEditingCodeId(null);
    setEditingMinutes("");
    setEditingRationale("");
  }, []);

  const handleSaveCode = useCallback(
    async (code: TProceduralCode) => {
      if (!editingCodeId || !noteId) return;

      const minutes = Number.parseInt(editingMinutes, 10);
      if (Number.isNaN(minutes) || minutes < 0) {
        message.error("Please enter a valid positive number for minutes.");
        return;
      }

      if (!editingRationale.trim()) {
        message.error("Explanation cannot be empty.");
        return;
      }

      setIsSaving(true);
      try {
        const updateData: { psychotherapy_duration_minutes?: number; rationale?: string } = {};

        // Check if minutes changed
        if (minutes !== code.additional_fields?.psychotherapy_duration_minutes) {
          updateData.psychotherapy_duration_minutes = minutes;
          trackEvent(EventType.UPDATE_PSYCHOTHERAPY_MINUTES, {
            originalMinutes: code.additional_fields?.psychotherapy_duration_minutes,
            newMinutes: minutes,
            audioId: noteId,
          });
        }

        // Check if rationale changed
        if (editingRationale.trim() !== code.rationale) {
          updateData.rationale = editingRationale.trim();
          trackEvent(EventType.UPDATE_PROCEDURAL_CODE_EXPLANATION, {
            audioId: noteId,
          });
        }

        if (Object.keys(updateData).length === 0) {
          message.info("No changes to save.");
          handleCancelEdit();
          return;
        }

        await updateProceduralCode(editingCodeId, updateData);

        // Refresh the notes details to get updated data
        const res = await getNotesDetails(noteId.toString());
        dispatch(setNotesDetails(res.data));

        message.success("Procedural code updated successfully.");
        setEditingCodeId(null);
        setEditingMinutes("");
        setEditingRationale("");
      } catch (error) {
        console.error("Error updating procedural code:", error);
        message.error("Failed to update procedural code. Please try again.");
      } finally {
        setIsSaving(false);
      }
    },
    [editingCodeId, editingMinutes, editingRationale, noteId, dispatch, handleCancelEdit],
  );

  // Helper function to check if we should use the default psychotherapy code
  const isDefaultPsychotherapyCode = useCallback((code: TProceduralCode) => {
    // Check if it has psychotherapy_duration_minutes
    return code.additional_fields?.psychotherapy_duration_minutes !== undefined;
  }, []);

  const isMDMComplexityCode = useCallback((code: TProceduralCode) => {
    // Check if it has psychotherapy_duration_minutes OR if it's an E/M code
    const emCodes = ["99212", "99213", "99214", "99215", "90792", "99203", "99204", "99205"];
    return emCodes.includes(code.code);
  }, []);

  // Helper function to get the display code
  const getDisplayCode = useCallback(
    (code: TProceduralCode) => {
      if (isDefaultPsychotherapyCode(code)) {
        if (getCptCodeByPsychotherapyMinutes(code.additional_fields?.psychotherapy_duration_minutes)) {
          return getCptCodeByPsychotherapyMinutes(code.additional_fields?.psychotherapy_duration_minutes)?.toString();
        }

        // This means the psychotherapy minutes is less than 16 minutes, so we use the default code
        return "90833";
      }
      return code.code;
    },
    [isDefaultPsychotherapyCode],
  );

  const addAdditionalExplanation = useCallback(
    (code: TProceduralCode) => {
      // Check if this code has psychotherapy_duration_minutes in additional_fields
      const psychotherapyMinutes = code.additional_fields?.psychotherapy_duration_minutes;

      // Add timing information if available
      if (currentNotesDetails?.stats_transcribe_start_at && psychotherapyMinutes && psychotherapyMinutes > 0) {
        const endTime = new Date(currentNotesDetails.stats_transcribe_start_at);
        const startTime = new Date(endTime.getTime() - psychotherapyMinutes * 60 * 1000);

        return `\n\nIn this session, psychotherapy started at ${formatTimeInLocalTimezone(startTime.toISOString())} and ended at ${formatTimeInLocalTimezone(currentNotesDetails.stats_transcribe_start_at)}.`;
      }

      return "";
    },
    [currentNotesDetails],
  );

  const copyAllCodes = (note: TNotes) => {
    let text = "";
    const proceduralCodesText = buildProceduralCodesText(note);
    if (proceduralCodesText) {
      text += `${proceduralCodesText}`;
    }
    const diagnosticCodesText = buildDiagnosticCodesText(note);
    if (diagnosticCodesText) {
      text += `${diagnosticCodesText}`;
    }
    if (!text) return;
    navigator.clipboard.writeText(text);
    message.success("All codes copied to clipboard!");
  };

  const copyProceduralCodesDetails = (note: TNotes) => {
    const codesText = buildProceduralCodesText(note);
    if (!codesText) return;
    navigator.clipboard.writeText(codesText);
    message.success("Procedural codes copied to clipboard!");
  };

  const copyDiagnosticCodesDetails = (note: TNotes) => {
    const codesText = buildDiagnosticCodesText(note);
    if (!codesText) return;
    navigator.clipboard.writeText(codesText);
    message.success("Diagnostic codes copied to clipboard!");
  };

  const renderProceduralSection = () => {
    if (isLoadingCodes) {
      return (
        <Flex align="center">
          <LoadingOutlined />
          <Typography
            style={{
              marginLeft: "10px",
              fontWeight: "bold",
              color: "#606060",
            }}
          >
            Your Nudge Coding assistant is generating procedural codes
          </Typography>
        </Flex>
      );
    }

    if (!currentNotesDetails) {
      return null;
    }

    if (proceduralStatus === ProceduralCodeStatus.FAILED) {
      return (
        <Alert
          type="error"
          showIcon
          message="We couldn't generate procedural codes for this note. Please try regenerating the note."
        />
      );
    }

    if (proceduralStatus === ProceduralCodeStatus.NOT_PROCESSED) {
      return <Typography>Procedural codes were not requested for this note.</Typography>;
    }

    if (proceduralCodes.length === 0) {
      return (
        <Typography>
          No procedural codes were generated. Please regenerate the note to obtain codes by clicking on Edit Note.
        </Typography>
      );
    }

    return (
      <Card
        hoverable
        title="Procedural Codes"
        extra={<NoteCopyButton onClick={() => copyProceduralCodesDetails(currentNotesDetails)} />}
      >
        <div style={{ marginTop: "0px" }}>
          {proceduralCodes.map((code, index) => (
            <div
              key={code.id}
              style={{
                borderBottom: "1px solid #f0f0f0",
                paddingBottom: "15px",
                marginBottom: "15px",
                ...(index > 0 ? { marginTop: "20px" } : {}),
                ...(editingCodeId === code.id
                  ? {
                      backgroundColor: "#f9f9f9",
                      padding: "15px",
                      borderRadius: "8px",
                      border: "1px solid #d9d9d9",
                    }
                  : {}),
              }}
            >
              <Flex justify="space-between" align="center" style={{ marginBottom: 12 }}>
                <Flex align="center">
                  <span style={{ fontWeight: "bold" }}>
                    <strong>CPT Code: </strong>
                  </span>
                  <span style={{ marginLeft: "5px" }}>{getDisplayCode(code)}</span>
                </Flex>
                {isDefaultPsychotherapyCode(code) && editingCodeId !== code.id && (
                  <Button type="primary" onClick={() => handleEditCode(code)}>
                    Edit
                  </Button>
                )}
              </Flex>

              {!isDefaultPsychotherapyCode(code) && !isMDMComplexityCode(code) && (
                <Flex align="center" style={{ marginBottom: 8 }}>
                  <span style={{ fontWeight: "bold" }}>
                    <strong>Short Description: </strong>
                  </span>
                  <span style={{ marginLeft: "5px" }}>{code.short_description}</span>
                </Flex>
              )}

              {code.additional_fields?.psychotherapy_duration_minutes !== undefined && (
                <Flex align="center" style={{ marginBottom: 12 }}>
                  <span style={{ fontWeight: "bold" }}>
                    <strong>Psychotherapy mins: </strong>
                  </span>
                  {editingCodeId === code.id ? (
                    <Input
                      type="number"
                      min="0"
                      value={editingMinutes}
                      onChange={(e) => setEditingMinutes(e.target.value)}
                      style={{ width: "80px", marginLeft: "5px" }}
                      placeholder="Minutes"
                    />
                  ) : (
                    <span style={{ marginLeft: "5px" }}>{code.additional_fields.psychotherapy_duration_minutes}</span>
                  )}
                </Flex>
              )}

              <Flex vertical>
                <span style={{ fontWeight: "bold", marginBottom: 8 }}>
                  <strong>Explanation:</strong>
                </span>
                {editingCodeId === code.id ? (
                  <div>
                    <TextArea
                      value={editingRationale}
                      onChange={(e) => setEditingRationale(e.target.value)}
                      placeholder="Enter explanation for this procedural code..."
                      autoSize={{ minRows: 3, maxRows: 6 }}
                      style={{ marginBottom: 12 }}
                    />
                    <Flex gap={8} justify="flex-end">
                      <Button onClick={handleCancelEdit} disabled={isSaving}>
                        Cancel
                      </Button>
                      <Button type="primary" onClick={() => handleSaveCode(code)} loading={isSaving}>
                        Save Changes
                      </Button>
                    </Flex>
                  </div>
                ) : (
                  <span style={{ lineHeight: 1.5 }}>
                    {code.rationale}
                    {addAdditionalExplanation(code)}
                  </span>
                )}
              </Flex>
            </div>
          ))}
        </div>
      </Card>
    );
  };

  const renderDiagnosticSection = () => {
    if (!currentNotesDetails) {
      return null;
    }

    if (isLoadingDiagnosticCodes) {
      return (
        <Flex align="center">
          <LoadingOutlined />
          <Typography
            style={{
              marginLeft: "10px",
              fontWeight: "bold",
              color: "#606060",
            }}
          >
            Your Nudge Coding assistant is generating diagnostic codes
          </Typography>
        </Flex>
      );
    }

    if (diagnosticStatus === DiagnosticCodeStatus.FAILED) {
      return (
        <Alert
          type="error"
          showIcon
          message="We couldn't generate diagnostic codes for this note. Please try regenerating the note."
        />
      );
    }

    if (diagnosticStatus === DiagnosticCodeStatus.NOT_PROCESSED) {
      return <Typography>Diagnostic codes were not requested for this note.</Typography>;
    }

    if (diagnosticCodes.length === 0) {
      return <Typography>No diagnostic codes were generated for this note.</Typography>;
    }

    return (
      <Card
        hoverable
        title="Diagnostic Codes (ICD-10)"
        extra={<NoteCopyButton onClick={() => copyDiagnosticCodesDetails(currentNotesDetails)} />}
      >
        <div style={{ marginTop: "0px" }}>
          {diagnosticCodes.map((code: TDiagnosticCode, index: number) => (
            <div
              key={code.id}
              style={{
                borderBottom: "1px solid #f0f0f0",
                paddingBottom: "15px",
                marginBottom: "15px",
                ...(index > 0 ? { marginTop: "20px" } : {}),
              }}
            >
              <Flex vertical gap={8}>
                <Flex align="center">
                  <span style={{ fontWeight: "bold" }}>
                    <strong>ICD-10 Code: </strong>
                  </span>
                  <span style={{ marginLeft: "5px" }}>{code.code}</span>
                </Flex>
                {code.short_description && (
                  <div>
                    <span style={{ fontWeight: "bold" }}>
                      <strong>Description: </strong>
                    </span>
                    <span style={{ marginLeft: "5px" }}>{code.short_description}</span>
                  </div>
                )}
                {code.additional_fields?.confidence && (
                  <div>
                    <span style={{ fontWeight: "bold" }}>
                      <strong>Confidence: </strong>
                    </span>
                    <span style={{ marginLeft: "5px" }}>{code.additional_fields.confidence}</span>
                  </div>
                )}
                {code.rationale && (
                  <div>
                    <span style={{ fontWeight: "bold" }}>
                      <strong>Rationale: </strong>
                    </span>
                    <span style={{ marginLeft: "5px", lineHeight: 1.5 }}>{code.rationale}</span>
                  </div>
                )}
              </Flex>
            </div>
          ))}
        </div>
      </Card>
    );
  };

  return (
    <Card
      style={{ marginTop: 20, background: "#f9fafb" }}
      styles={{
        body: { background: "#fff" },
        header: { backgroundColor: "#fafafc", borderBottom: "2px solid #f0f0f0" },
      }}
      title={
        <Flex className={`${styles[`${componentName}__title`]}`}>
          <Flex>
            <span>
              <svg width="16" height="12" viewBox="0 0 20 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M17.1997 5.59961C18.5252 5.59961 19.6001 6.67452 19.6001 8V12.7998C19.6001 14.1253 18.5252 15.2002 17.1997 15.2002H7.6001C6.27461 15.2002 5.19971 14.1253 5.19971 12.7998V8C5.19971 6.67452 6.27461 5.59961 7.6001 5.59961H17.1997ZM12.3999 8C11.0745 8.00005 10.0005 9.07494 10.0005 10.4004C10.0007 11.7257 11.0746 12.7998 12.3999 12.7998C13.7253 12.7998 14.8001 11.7257 14.8003 10.4004C14.8003 9.07491 13.7254 8 12.3999 8ZM12.3999 0.799805C13.7254 0.799805 14.8003 1.87471 14.8003 3.2002H2.80029V10.3994L2.5542 10.3877C1.34419 10.2647 0.400001 9.24245 0.399902 8V3.2002C0.399902 1.87471 1.47481 0.799805 2.80029 0.799805H12.3999Z"
                  fill="#9CA3AF"
                />
              </svg>
              &nbsp;Smart Coding
            </span>
          </Flex>
          <Flex>
            <Flex gap={10} className={`${styles[`${componentName}__button-container`]}`}>
              <CopyButton onClick={() => copyAllCodes(currentNotesDetails)} title="Copy Codes" />
            </Flex>
          </Flex>
        </Flex>
      }
    >
      <Alert
        message={`Confirm codes and explanations as per your best judgement. The specialty used for you was: ${userInfo?.specialty_label}`}
        type="warning"
        style={{ marginBottom: "20px", color: "#B45309", background: "#FFFBEB", border: "1px solid #FDE68A" }}
        action={
          <Button
            type="link"
            onClick={() => {
              dispatch(setShowSettingsModal(true));
            }}
            style={{
              color: "#B45309",
            }}
          >
            Edit
          </Button>
        }
      />
      <Flex vertical gap={20}>
        {renderProceduralSection()}
        {isNonBehavioralHealthUser(userInfo?.practice_area) && renderDiagnosticSection()}
      </Flex>
    </Card>
  );
};

export default ProceduralCoding;
